import React from 'react';
import html2pdf from 'html2pdf.js';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { I18nextProvider } from 'react-i18next';
import i18n from '../i18n/config';
import { DEFAULT_LANGUAGE } from '../i18n/constants';

/**
 * Converts an image URL to a data URL for PDF embedding.
 * Tries direct fetch, then Image/canvas fallback.
 * Returns null on failure so callers can degrade gracefully.
 */
const toDataUrl = async function (url: string): Promise<string | null> {
    const blobToDataUrl = (blob: Blob): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(blob);
        });

    const fetchBlob = async (targetUrl: string): Promise<Blob | null> => {
        try {
            const response = await fetch(targetUrl);
            if (!response.ok) return null;
            const blob = await response.blob();
            if (!blob.type.startsWith('image/')) return null;
            return blob;
        } catch {
            return null;
        }
    };

    // Method 1: direct fetch (works when same-origin or CORS allows)
    const directBlob = await fetchBlob(url);
    if (directBlob) return await blobToDataUrl(directBlob);

    // Method 2: Image + canvas (works when image is CORS-enabled or same-origin)
    let dataUrl: string | null = null;
    try {
        dataUrl = await new Promise<string | null>(resolve => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            const timeout = setTimeout(() => {
                img.onload = null;
                img.onerror = null;
                img.src = '';
                resolve(null);
            }, 3000);
            img.onload = () => {
                clearTimeout(timeout);
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.naturalWidth;
                    canvas.height = img.naturalHeight;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        resolve(null);
                        return;
                    }
                    ctx.drawImage(img, 0, 0);
                    resolve(canvas.toDataURL('image/jpeg', 0.92));
                } catch {
                    resolve(null);
                }
            };
            img.onerror = () => {
                clearTimeout(timeout);
                resolve(null);
            };
            img.src = url;
        });
        if (dataUrl !== null) return dataUrl;
    } catch {
        // Fall through and return null (expected for CORS-blocked / invalid URLs; no Sentry noise)
    }

    return null;
};

const generatePDF = async (
    component: React.ReactNode,
    filename: string,
    language: string = DEFAULT_LANGUAGE,
): Promise<void> => {
    console.log('generatePDF', language);
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    container.style.width = '600px';
    container.style.backgroundColor = 'transparent';
    container.style.padding = '20px';
    document.body.appendChild(container);

    // Create a cloned i18n instance to avoid affecting the main app's language
    const clonedI18n = i18n.cloneInstance();
    const originalLanguage = clonedI18n.language;

    // Set the language for PDF generation
    await clonedI18n.changeLanguage(language);

    const root = createRoot(container);
    root.render(
        <I18nextProvider i18n={clonedI18n}>{component}</I18nextProvider>,
    );

    try {
        await new Promise(resolve => setTimeout(resolve, 1000));

        const opt = {
            margin: 0,
            filename: filename,
            enableLinks: true,
            image: { type: 'jpeg' as const, quality: 0.95 },
            html2canvas: { scale: 1 },
            jsPDF: {
                unit: 'in',
                format: 'letter',
                orientation: 'portrait' as const,
            },
            pagebreak: { after: '.pageBreakAfter', avoid: 'img' },
        };

        await html2pdf().set(opt).from(container.innerHTML).save();
    } catch (error) {
        Sentry.withScope(scope => {
            scope.setTag('context', 'pdf-generation');
            scope.setLevel('error');
            Sentry.captureException(error);
        });
    } finally {
        root.unmount();
        document.body.removeChild(container);
        // Restore original language on cloned instance (cleanup)
        await clonedI18n.changeLanguage(originalLanguage);
    }
};

export { generatePDF, toDataUrl };
